/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { useState, useRef } from "react";

import dynamic from "next/dynamic";
import { useOnClickOutside } from "usehooks-ts";

import { CodeBlock } from "src/classes/block/block-types";
import LanguageMenu from "src/components/Block/BlockTypes/CodeBlock/lib/LanguageMenu";
import {
  StyledCodeBlockContent,
  StyledLanguage,
  codeEditorStyles,
} from "src/components/Block/BlockTypes/CodeBlock/styles";
import { KeyCodes } from "src/components/Hotkeys/hotkeys";
import { NoSSR } from "src/components/UI/NoSSR/NoSSR";

// TODO: replace with a high quality package
const CodeEditor = dynamic(() => import("@uiw/react-textarea-code-editor").then((mod) => mod.default) as any, {
  ssr: false,
}) as any;

interface Props {
  block: CodeBlock;
  editable: boolean;
}

export const CodeBlockContent = ({ block, editable }: Props) => {
  const languageMenuRef = useRef(null);
  const codeBlockRef = useRef(null);

  const language = block.observe((state) => state.language);
  const [dropDownOpen, setDropDownOpen] = useState(false);

  useOnClickOutside(languageMenuRef, () => editable && setDropDownOpen(false));

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.code) {
      case KeyCodes.ESCAPE:
        e.stopPropagation();
        break;
      case KeyCodes.ARROW_LEFT:
      case KeyCodes.ARROW_RIGHT:
      case KeyCodes.ARROW_UP:
      case KeyCodes.ARROW_DOWN:
      case KeyCodes.BACKSPACE:
        e.stopPropagation();
        break;
      default:
        // keep default behavior for hot keys
        if (e.metaKey) e.stopPropagation();
        break;
    }
  };

  return (
    <NoSSR>
      <StyledCodeBlockContent ref={codeBlockRef}>
        <CodeEditor
          className="CodeEditor"
          id={block.codeEitorId}
          value={block.snapshot.plainText}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => block.updatePlaintext(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter some code"
          language={language}
          padding={25}
          readOnly={!editable}
          style={codeEditorStyles}
        />
        <div ref={languageMenuRef}>
          <StyledLanguage
            onClick={() => editable && setDropDownOpen(!dropDownOpen)}
            style={{ cursor: !editable ? "default" : "pointer" }}
          >
            {language}
          </StyledLanguage>
          {dropDownOpen && <LanguageMenu block={block} closeMenu={() => setDropDownOpen(false)} />}
        </div>
      </StyledCodeBlockContent>
    </NoSSR>
  );
};
